/**
 * Reading one day off a chart by pointing at it.
 *
 * Every chart on the overview already answers this without a script. Each
 * point carries an invisible band the width of one step, and each band holds
 * a `<title>` saying what that day was worth - which is a tooltip every
 * browser draws on its own. A page whose script never arrives loses nothing
 * except the speed of it.
 *
 * What is added here is what markup cannot do: an answer which appears at
 * once rather than after the second a browser waits, placed against the
 * chart rather than against the pointer, and a line down the chart marking
 * which day is being read. Those three are the difference between a tooltip
 * and being able to run along a month and watch the number change.
 *
 * The bands are the only thing the pointer is asked to hit. A stroke two
 * pixels wide is not something anybody can hold a mouse still on, so what
 * answers for a point is the whole column above and below it.
 */

import { query, queryAll } from "../core/dom";

/** How far from the edge of the chart the answer stops following the pointer. */
const MARGIN = 8;

/** Wire one chart. */
export function bindChartHover(chart: HTMLElement): void {
    const frame = query<HTMLElement>("[data-chart-frame]", chart);
    const readout = query<HTMLElement>("[data-chart-readout]", chart);
    const guide = query<SVGLineElement>("[data-chart-guide]", chart);
    const bands = queryAll<SVGRectElement>("[data-chart-hit]", chart);

    if (!frame || !readout || bands.length === 0) {
        return;
    }

    // The browser's own tooltip would arrive a second later and say the same
    // thing twice, so the titles are taken away once this is running.
    const words = new Map<SVGRectElement, string>();
    for (const band of bands) {
        const title = band.querySelector("title");
        words.set(band, title?.textContent ?? "");
        title?.remove();
    }

    const hide = (): void => {
        readout.hidden = true;
        if (guide) {
            guide.setAttribute("hidden", "");
        }
    };

    const show = (band: SVGRectElement): void => {
        readout.textContent = words.get(band) ?? "";
        readout.hidden = false;

        // Placed against the band rather than against the pointer, so it
        // holds still while the pointer moves inside one day, and pulled
        // back from either edge so it is never drawn half outside the panel.
        const box = frame.getBoundingClientRect();
        const over = band.getBoundingClientRect();
        const middle = over.left + over.width / 2 - box.left;
        const room = box.width - readout.offsetWidth - MARGIN;
        const at = Math.min(Math.max(MARGIN, middle - readout.offsetWidth / 2), Math.max(MARGIN, room));

        readout.style.left = `${at}px`;

        if (guide) {
            // Read off the attributes the server wrote rather than out of the
            // measured geometry: the numbers are the same, and an attribute is
            // there whether or not the drawing has been laid out yet.
            const left = Number(band.getAttribute("x") ?? 0);
            const wide = Number(band.getAttribute("width") ?? 0);
            const x = String(left + wide / 2);

            guide.setAttribute("x1", x);
            guide.setAttribute("x2", x);
            guide.removeAttribute("hidden");
        }
    };

    for (const band of bands) {
        band.addEventListener("pointerenter", () => show(band));
        // Reached by a keyboard as well: a chart which can only be read by
        // hovering is a chart somebody without a mouse cannot read at all.
        band.addEventListener("focus", () => show(band));
        band.addEventListener("blur", hide);
        band.setAttribute("tabindex", "0");
    }

    frame.addEventListener("pointerleave", hide);
}

/** Wire every chart of the current page. */
export function initChartHover(): void {
    for (const chart of queryAll<HTMLElement>("[data-chart]")) {
        bindChartHover(chart);
    }
}
